import { ArrowUp, Square } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { t } from '@/lib/i18n';
import { IconButton, Textarea } from '@/ui';
import s from './Assistant.module.css';
import { useAssistantState } from './store';

const finePointer = () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;

/** Enter sends on a desktop keyboard; on touch, Enter adds a new line (send with the button). */
export function Composer({
  onSend,
  onStop,
  streaming,
  disabled,
}: {
  onSend: (text: string) => void;
  onStop: () => void;
  streaming: boolean;
  disabled?: boolean;
}) {
  const { focusTick, draft } = useAssistantState();
  const [text, setText] = useState(draft ?? '');
  const [seenTick, setSeenTick] = useState(focusTick);
  const ref = useRef<HTMLTextAreaElement>(null);

  // "Ask about this" opens the panel with a prompt: take it over once per open request.
  if (seenTick !== focusTick) {
    setSeenTick(focusTick);
    if (draft) setText(draft);
  }

  useEffect(() => {
    ref.current?.focus();
  }, [focusTick]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || streaming || disabled) return;
    onSend(text);
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && finePointer()) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form className={s.composer} onSubmit={submit}>
      <Textarea
        ref={ref}
        autoGrow
        rows={1}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('Ask the assistant…')}
        aria-label={t('Message to the assistant')}
        disabled={disabled}
        className={s.input}
      />
      {streaming ? (
        <IconButton icon={Square} label={t('Stop')} variant="secondary" onClick={onStop} />
      ) : (
        <IconButton icon={ArrowUp} label={t('Send')} variant="primary" type="submit" disabled={!text.trim() || disabled} />
      )}
    </form>
  );
}
